'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Search, X, Calendar, MessageSquare, Users, ArrowRight } from 'lucide-react';
import { useQuickSearch, SearchResult } from '@/hooks/useQuickSearch';
import Link from 'next/link';

const TYPE_CONFIG = {
  schedule: { icon: Calendar, color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'Schedule' },
  room: { icon: MessageSquare, color: 'text-teal-500', bg: 'bg-teal-50', label: 'Room' },
  staff: { icon: Users, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Staff' },
};

const BRANCH_LABELS: Record<string, string> = {
  live_events: 'Production',
  studio: 'Education',
  outreach: 'Development',
  internal: 'Internal',
  education: 'Education',
  production: 'Production',
  development: 'Development',
  leadership: 'Leadership',
};

export default function QuickSearch() {
  const { query, results, loading, search, clear } = useQuickSearch();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        clear();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [clear]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    search(val);
    setOpen(val.length > 0);
  }

  function handleClear() {
    clear();
    setOpen(false);
    inputRef.current?.focus();
  }

  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.length > 0 && setOpen(true)}
          placeholder="Search schedule, rooms, staff…"
          className="w-full pl-8 pr-8 py-2 text-[12.5px] bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 placeholder:text-slate-300 transition-all"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
          >
            <X size={13} />
          </button>
        )}
        {!query && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-300 font-mono hidden sm:block">
            ⌘K
          </span>
        )}
      </div>

      {open && (results.length > 0 || loading) && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-stone-200 rounded-xl shadow-lg z-50 overflow-hidden max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-[12px] text-slate-400">Searching…</div>
          )}

          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-3 text-[12px] text-slate-400">No results for "{query}"</div>
          )}

          {!loading && Object.entries(grouped).map(([type, items]) => {
            const cfg = TYPE_CONFIG[type as keyof typeof TYPE_CONFIG];
            const TypeIcon = cfg.icon;
            return (
              <div key={type}>
                <div className="px-3 py-1.5 bg-stone-50 border-b border-stone-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    {cfg.label}
                  </p>
                </div>
                {items.map((result) => (
                  <Link
                    key={result.id}
                    href={result.href ?? '#'}
                    onClick={() => { setOpen(false); clear(); }}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-stone-50 transition-colors group"
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                      <TypeIcon size={13} className={cfg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-slate-700 truncate">{result.title}</p>
                      <p className="text-[11px] text-slate-400 truncate">{result.subtitle}</p>
                    </div>
                    {result.badge && (
                      <span className="text-[10px] font-medium text-slate-400 shrink-0">
                        {BRANCH_LABELS[result.badge] ?? result.badge}
                      </span>
                    )}
                    <ArrowRight size={12} className="text-slate-200 group-hover:text-slate-400 transition-colors shrink-0" />
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
